import type { BillingBucket, TicketType } from '@t3/shared';

export type { BillingBucket };

// Buckets that can be logged WITHOUT a ticket. block_hours and project
// require an organization (advising meetings, project meetings) — the
// other three are T3-internal time.
export const TICKETLESS_BUCKETS: BillingBucket[] = [
  'block_hours',
  'project',
  'non_billable_pm',
  'non_billable_internal',
  'comp_time_used',
];

// Ticketless buckets that bill against a client org (block-hour
// subscription consumption or project budget) and therefore require an
// organizationId in the payload.
export const TICKETLESS_BILLABLE_BUCKETS = new Set<BillingBucket>([
  'block_hours',
  'project',
]);

// Buckets that consume client hours. Matches the billable/non-billable split
// the timesheet summary and technician reports use.
export const BILLABLE_BUCKETS = new Set<BillingBucket>([
  'block_hours',
  'project',
  'quote',
]);

export interface BucketOption {
  value: BillingBucket;
  label: string;
  /** shown in the ticketed bucket-override dropdown */
  inTicketed: boolean;
  /** shown in the ticketless category dropdown */
  inTicketless: boolean;
}

export const ALL_BUCKETS: BucketOption[] = [
  { value: 'block_hours', label: 'Block hours (advising / client meeting)', inTicketed: true, inTicketless: true },
  { value: 'project', label: 'Project (kickoff, status, planning)', inTicketed: true, inTicketless: true },
  { value: 'quote', label: 'Quote', inTicketed: true, inTicketless: false },
  { value: 'chat_assist', label: 'Chat assistance', inTicketed: true, inTicketless: false },
  { value: 'non_billable', label: 'Non-billable (legacy)', inTicketed: true, inTicketless: false },
  { value: 'non_billable_pm', label: 'Non-billable PM — check-ins, onboarding', inTicketed: false, inTicketless: true },
  { value: 'non_billable_internal', label: 'Non-billable internal — team, learning', inTicketed: false, inTicketless: true },
  { value: 'comp_time_used', label: 'Comp time used', inTicketed: false, inTicketless: true },
];

/** Short label for tight spaces (the mini timer). */
export const BUCKET_SHORT_LABELS: Record<BillingBucket, string> = {
  block_hours: 'Block hours',
  project: 'Project',
  quote: 'Quote',
  chat_assist: 'Chat assistance',
  non_billable: 'Non-billable',
  non_billable_pm: 'Non-billable PM',
  non_billable_internal: 'Non-billable internal',
  comp_time_used: 'Comp time used',
};

export function bucketLabel(bucket: BillingBucket): string {
  return BUCKET_SHORT_LABELS[bucket];
}

export function isBillableBucket(bucket: BillingBucket): boolean {
  return BILLABLE_BUCKETS.has(bucket);
}

// Mirrors defaultBillingBucketForType() in the backend's ticketRules.ts. Used
// only to preview what a ticket WILL bill as before the timer starts — the
// server still derives the real value at commit (and knows about the
// project-billing-mode override this can't see).
export function derivedBucketForTicketType(type: TicketType | null): BillingBucket | null {
  switch (type) {
    case 'escalation':
      return 'block_hours';
    case 'project':
      return 'project';
    case 'quote':
      return 'quote';
    case 'chat':
      return 'chat_assist';
    case 'administrative':
      return 'non_billable_pm';
    default:
      return null;
  }
}

/**
 * Resolve the Billable / Non-billable toggle to a concrete bucket.
 *
 * The toggle opens on whichever side the ticket already bills as, so it never
 * silently contradicts the ticket type — a chat ticket opens on Non-billable
 * (chat_assist), an escalation on Billable (block_hours). Flipping it swaps to
 * that side's counterpart.
 */
export function bucketForToggle(args: {
  billable: boolean;
  derived: BillingBucket | null;
  hasTicket: boolean;
}): BillingBucket {
  const { billable, derived, hasTicket } = args;
  if (!hasTicket) {
    // Ticketless: one sensible default per side. `other…` reaches the rest.
    return billable ? 'block_hours' : 'non_billable_internal';
  }
  if (billable) {
    return derived && BILLABLE_BUCKETS.has(derived) ? derived : 'block_hours';
  }
  return derived && !BILLABLE_BUCKETS.has(derived) ? derived : 'non_billable';
}
